import React, { useMemo } from 'react';

import { Link } from 'gatsby';

import { styled } from '@mui/material';

import { ServiceSegmentType } from '@mewa/types';
import {
  ASPECT_RATIO_LANDSCAPE,
  LinkedImageCardSlide,
  OverviewSlider,
  ResponsiveProductGrid,
  useSliderViewport,
} from '@mewa/ui';

import t from '../../../libs/translate';
import { SearchPageTranslations } from '../../components/header/types';
import Image from '../../components/image';
import {
  PageFindResultData,
  searchResultIsIndustry,
  searchResultIsServiceSegment,
} from '../../components/search/types';
import { jpgPath } from '../../helpers/image-factory';

export type ServiceSegmentImages = Record<
  keyof typeof ServiceSegmentType,
  { image: string; hoverImage: string }
>;

const imageWidth = 552;
const imageHeight = 414;

const fourByThreeCardImages = (src: string) => [
  {
    src,
    media: '',
    width: imageWidth,
    height: imageHeight,
  },
];

export const SearchResultCategories = React.memo(
  ({
    results,
    expanded,
    serviceSegmentImages,
    translations,
  }: {
    results: PageFindResultData[];
    expanded: boolean;
    serviceSegmentImages?: ServiceSegmentImages;
    translations: SearchPageTranslations;
  }) => {
    const sliderViewport = useSliderViewport();

    const slides = useMemo(
      () =>
        results.map((result) => {
          let imageSrc = result.meta.image ?? '';
          let imageSrcHover = result.meta.image ?? '';
          if (searchResultIsServiceSegment(result) && serviceSegmentImages) {
            imageSrc = jpgPath(
              serviceSegmentImages[result.meta.service_segment_type].image,
              imageWidth * 2,
              imageHeight * 2,
            );
            imageSrcHover = jpgPath(
              serviceSegmentImages[result.meta.service_segment_type].hoverImage,
              imageWidth * 2,
              imageHeight * 2,
            );
          }
          const linkDescription =
            result.meta.page_type === 'collection'
              ? t(translations, 'general_collection')
              : result.meta.page_type === 'category'
                ? t(translations, 'search_page_category_link', { amount: '' })
                : searchResultIsIndustry(result)
                  ? result.meta.service_segment_name
                  : '';

          const title = searchResultIsIndustry(result)
            ? result.meta.industry_name
            : result.meta.title;

          return {
            image: (
              <StyledImage images={fourByThreeCardImages(imageSrc)} alt="" />
            ),
            hoverImage: (
              <StyledImage
                images={fourByThreeCardImages(imageSrcHover)}
                alt=""
              />
            ),
            wrapperLink: <Link to={result.url}>{title}</Link>,
            linkDescription,
          };
        }),
      [serviceSegmentImages, results, translations],
    );

    const sliderView = useMemo(
      () => (
        <OverviewSlider
          viewport={sliderViewport}
          aspectRatio={ASPECT_RATIO_LANDSCAPE}
          slides={slides.map((slide) => ({
            ...slide,
            linkDescription: slide.linkDescription,
          }))}
        ></OverviewSlider>
      ),
      [sliderViewport, slides],
    );

    const gridView = useMemo(
      () => (
        <StyledResponsiveProductGrid>
          {slides.map((slide, idx) => (
            <StyledLinkedImageCardSlide
              key={idx}
              aspectRatio={ASPECT_RATIO_LANDSCAPE}
              image={slide.image}
              wrapperLink={slide.wrapperLink}
              linkDescription={slide.linkDescription}
            />
          ))}
        </StyledResponsiveProductGrid>
      ),
      [slides],
    );

    return expanded ? gridView : sliderView;
  },
);

const StyledImage = styled(Image)({
  img: {
    objectPosition: 'top',
  },
});

const StyledResponsiveProductGrid = styled(ResponsiveProductGrid)({
  gridGap: '20px',
});

const StyledLinkedImageCardSlide = styled(LinkedImageCardSlide)({
  border: 'none',
});
